import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {RefrigeratorUnit} from '../models/refrigerator/refrigeratorUnit.model';
import {RefrigeratorComponent} from '../models/refrigerator/refrigeratorComponent.model';
import {RefrigeratorService} from '../services/refrigerator.service';
import {Refrigerator} from '../actions/refrigerator.actions';
import {JobRefrigeratorUnit} from '../models/refrigerator/jobRefrigeratorUnit.model';
import {JobRefrigerator} from '../models/refrigerator/jobRefrigerator.model';
import ClearState = Auth.ClearState;
import GetRefrigeratorUnits = Refrigerator.GetRefrigeratorUnits;
import GetRefrigeratorUnit = Refrigerator.GetRefrigeratorUnit;
import GetRefrigeratorComponents = Refrigerator.GetRefrigeratorComponents;
import GetRefrigeratorComponent = Refrigerator.GetRefrigeratorComponent;
import GetJobRefrigeratorUnits = Refrigerator.GetJobRefrigeratorUnits;
import GetJobRefrigeratorUnit = Refrigerator.GetJobRefrigeratorUnit;
import CreateRefrigeratorUnit = Refrigerator.CreateRefrigeratorUnit;
import UpdateRefrigeratorUnit = Refrigerator.UpdateRefrigeratorUnit;
import DeleteRefrigeratorUnit = Refrigerator.DeleteRefrigeratorUnit;
import CreateRefrigeratorComponent = Refrigerator.CreateRefrigeratorComponent;
import UpdateRefrigeratorComponent = Refrigerator.UpdateRefrigeratorComponent;
import DeleteRefrigeratorComponent = Refrigerator.DeleteRefrigeratorComponent;
import UpdateJobRefrigeratorUnits = Refrigerator.UpdateJobRefrigeratorUnits;
import AssignRefrigeratorSerialNumbers = Refrigerator.AssignRefrigeratorSerialNumbers;
import ResetQuoteUnitsPricing = Refrigerator.ResetQuoteUnitsPricing;

export interface RefrigeratorStateModel {
  refrigeratorUnits: Array<RefrigeratorUnit>;
  currentRefrigeratorUnit: RefrigeratorUnit;
  refrigeratorComponents: Array<RefrigeratorComponent>;
  currentRefrigeratorComponent: RefrigeratorComponent;
  jobRefrigerators: JobRefrigerator;
  currentJobRefrigerator: JobRefrigeratorUnit;
}

@State<RefrigeratorStateModel>({
  name: 'refrigerator',
  defaults: {
    refrigeratorUnits: [],
    currentRefrigeratorUnit: null,
    refrigeratorComponents: [],
    currentRefrigeratorComponent: null,
    jobRefrigerators: null,
    currentJobRefrigerator: null,
  }
})
@Injectable()
export class RefrigeratorState {
  constructor(private refrigeratorService: RefrigeratorService
  ) {
  }

  @Selector()
  static refrigeratorUnits(state: RefrigeratorStateModel): Array<RefrigeratorUnit> {
    return state.refrigeratorUnits;
  }

  @Selector()
  static currentRefrigeratorUnit(state: RefrigeratorStateModel): RefrigeratorUnit {
    return state.currentRefrigeratorUnit;
  }

  @Selector()
  static refrigeratorComponents(state: RefrigeratorStateModel): Array<RefrigeratorComponent> {
    return state.refrigeratorComponents;
  }

  @Selector()
  static currentRefrigeratorComponent(state: RefrigeratorStateModel): RefrigeratorComponent {
    return state.currentRefrigeratorComponent;
  }

  @Selector()
  static jobRefrigerators(state: RefrigeratorStateModel): JobRefrigerator {
    return state.jobRefrigerators;
  }

  @Action(CreateRefrigeratorUnit)
  createRefrigeratorUnit(ctx: StateContext<RefrigeratorStateModel>, action: CreateRefrigeratorUnit): Observable<RefrigeratorUnit> {
    return this.refrigeratorService.createRefrigeratorUnits(action.unit).pipe(
      tap(unit => {
        ctx.patchState({
          currentRefrigeratorUnit: unit,
        });
      })
    );
  }

  @Action(UpdateRefrigeratorUnit)
  updateRefrigeratorUnit(ctx: StateContext<RefrigeratorStateModel>, action: UpdateRefrigeratorUnit): Observable<RefrigeratorUnit> {
    return this.refrigeratorService.updateRefrigeratorUnits(action.unit).pipe(
      tap(unit => {
        ctx.patchState({
          currentRefrigeratorUnit: unit
        });
      })
    );
  }

  @Action(DeleteRefrigeratorUnit)
  deleteRefrigeratorUnit(ctx: StateContext<RefrigeratorStateModel>, action: DeleteRefrigeratorUnit): Observable<RefrigeratorUnit> {
    const units = ctx.getState().refrigeratorUnits;
    const index = units.indexOf(action.unit);
    return this.refrigeratorService.deleteRefrigeratorUnits(action.unit.id).pipe(
      tap(unit => {
        ctx.patchState({
          currentRefrigeratorUnit: null,
          refrigeratorUnits: [
            ...units.slice(0, index),
            ...index === units.length - 1 ? [] : units.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(GetRefrigeratorUnits)
  getRefrigeratorUnits(ctx: StateContext<RefrigeratorStateModel>, action: GetRefrigeratorUnits): Observable<RefrigeratorUnit[]> {
    if (!action.force) {
      const refrigerators = ctx.getState().refrigeratorUnits;
      if (refrigerators.length > 0) {
        return of(refrigerators);
      }
    }
    return this.refrigeratorService.getRefrigeratorUnits().pipe(
      tap(units => {
        ctx.patchState({
          refrigeratorUnits: units
        });
      })
    );
  }

  @Action(GetRefrigeratorUnit)
  getRefrigeratorUnit(ctx: StateContext<RefrigeratorStateModel>, action: GetRefrigeratorUnit): Observable<RefrigeratorUnit> {
    if (!action.force) {
      const refrigerator = ctx.getState().currentRefrigeratorUnit;
      if (refrigerator && refrigerator.id === action.id) {
        return of(refrigerator);
      }

      const cachedUnit = ctx.getState().refrigeratorUnits.find(u => u.id === action.id);
      if (cachedUnit) {
        ctx.patchState({
          currentRefrigeratorUnit: cachedUnit
        });
        return of(cachedUnit);
      }
    }
    return this.refrigeratorService.getRefrigeratorUnit(action.id).pipe(
      tap(unit => {
        ctx.patchState({
          currentRefrigeratorUnit: unit
        });
      })
    );
  }

  @Action(CreateRefrigeratorComponent)
  createRefrigeratorComponent(ctx: StateContext<RefrigeratorStateModel>, action: CreateRefrigeratorComponent): Observable<RefrigeratorComponent> {
    return this.refrigeratorService.createRefrigeratorComponent(action.component).pipe(
      tap(component => {
        ctx.patchState({
          currentRefrigeratorComponent: component,
        });
      })
    );
  }

  @Action(UpdateRefrigeratorComponent)
  updateRefrigeratorComponent(ctx: StateContext<RefrigeratorStateModel>, action: UpdateRefrigeratorComponent): Observable<RefrigeratorComponent> {
    return this.refrigeratorService.updateRefrigeratorComponent(action.component).pipe(
      tap(component => {
        ctx.patchState({
          currentRefrigeratorComponent: component
        });
      })
    );
  }

  @Action(DeleteRefrigeratorComponent)
  deleteRefrigeratorComponent(ctx: StateContext<RefrigeratorStateModel>, action: DeleteRefrigeratorComponent): Observable<RefrigeratorComponent> {
    const components = ctx.getState().refrigeratorComponents;
    const index = components.indexOf(action.component);
    return this.refrigeratorService.deleteRefrigeratorComponent(action.component.id).pipe(
      tap(component => {
        ctx.patchState({
          currentRefrigeratorComponent: null,
          refrigeratorComponents: [
            ...components.slice(0, index),
            ...index === components.length - 1 ? [] : components.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(GetRefrigeratorComponents)
  getRefrigeratorComponents(ctx: StateContext<RefrigeratorStateModel>, action: GetRefrigeratorComponents): Observable<RefrigeratorComponent[]> {
    if (!action.force) {
      const components = ctx.getState().refrigeratorComponents;
      if (components.length > 0) {
        return of(components);
      }
    }
    return this.refrigeratorService.getRefrigeratorComponents().pipe(
      tap(components => {
        ctx.patchState({
          refrigeratorComponents: components
        });
      })
    );
  }

  @Action(GetRefrigeratorComponent)
  getRefrigeratorComponent(ctx: StateContext<RefrigeratorStateModel>, action: GetRefrigeratorComponent): Observable<RefrigeratorComponent> {
    if (!action.force) {
      const component = ctx.getState().currentRefrigeratorComponent;
      if (component && component.id === action.id) {
        return of(component);
      }

      const cachedComponent = ctx.getState().refrigeratorComponents.find(c => c.id === action.id);
      if (cachedComponent) {
        ctx.patchState({
          currentRefrigeratorComponent: cachedComponent
        });
        return of(cachedComponent);
      }
    }
    return this.refrigeratorService.getRefrigeratorComponent(action.id).pipe(
      tap(component => {
        ctx.patchState({
          currentRefrigeratorComponent: component
        });
      })
    );
  }

  @Action(GetJobRefrigeratorUnits)
  getJobRefrigeratorUnits(ctx: StateContext<RefrigeratorStateModel>, action: GetJobRefrigeratorUnits): Observable<Array<JobRefrigeratorUnit>> {
    if (!action.force) {
      const jobRefrigerators = ctx.getState().jobRefrigerators;
      if (jobRefrigerators && jobRefrigerators.id === action.id) {
        return of(jobRefrigerators.units);
      }

    }
    return this.refrigeratorService.getJobRefrigeratorUnits(action.id).pipe(
      tap(jobRefrigerator => {
        ctx.patchState({
          jobRefrigerators: jobRefrigerator
        });
      }),
      map(jobRefrigerator => jobRefrigerator.units)
    );
  }

  @Action(GetJobRefrigeratorUnit)
  getJobRefrigeratorUnit(ctx: StateContext<RefrigeratorStateModel>, action: GetJobRefrigeratorUnit): Observable<JobRefrigeratorUnit> {
    if (!action.force) {
      const jobRefrigerator = ctx.getState().currentJobRefrigerator;
      if (jobRefrigerator && jobRefrigerator.id === action.id) {
        return of(jobRefrigerator);
      }

      const jobRefrigerators = ctx.getState().jobRefrigerators;

      const cachedJobRefrigerator = jobRefrigerators ? jobRefrigerators.id === action.id && jobRefrigerators.units.find(u => u.id === action.unitId) : null;
      if (cachedJobRefrigerator) {
        ctx.patchState({
          currentJobRefrigerator: cachedJobRefrigerator
        });
        return of(cachedJobRefrigerator);
      }
    }
    return this.refrigeratorService.getJobRefrigeratorUnit(action.id, action.unitId).pipe(
      tap(unit => {
        ctx.patchState({
          currentJobRefrigerator: unit
        });
      })
    );
  }

  @Action(UpdateJobRefrigeratorUnits)
  updateJobRefrigeratorUnits(ctx: StateContext<RefrigeratorStateModel>, action: UpdateJobRefrigeratorUnits): Observable<JobRefrigerator> {
    return this.refrigeratorService.updateJobRefrigeratorUnits(action).pipe(
      tap(res => {
        ctx.patchState({
          jobRefrigerators: res
        });
      })
    );
  }

  @Action(AssignRefrigeratorSerialNumbers)
  assignSerialNumbers(ctx: StateContext<RefrigeratorStateModel>, action: AssignRefrigeratorSerialNumbers): Observable<JobRefrigerator> {
    return this.refrigeratorService.assignSerialNumbers(action.id).pipe(
      tap(res => {
        ctx.patchState({
          jobRefrigerators: res
        });
      })
    );
  }

  @Action(ResetQuoteUnitsPricing)
  resetQuoteUnitsPricing(ctx: StateContext<RefrigeratorStateModel>, action: ResetQuoteUnitsPricing): Observable<JobRefrigerator> {
    return this.refrigeratorService.resetQuotePricing(action.id).pipe(
      tap(res => {
        ctx.patchState({
          jobRefrigerators: res
        });
      })
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<RefrigeratorStateModel>): void {
    ctx.setState({
      refrigeratorUnits: [],
      currentRefrigeratorUnit: null,
      refrigeratorComponents: [],
      currentRefrigeratorComponent: null,
      jobRefrigerators: null,
      currentJobRefrigerator: null,
    });
  }
}
